import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Bell, X, Clock } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function NotificationsPanel() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      const res = await API.get("/notifications");
      setNotifications(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSnooze = async (id, days) => {
    await API.patch(`/notifications/${id}/snooze`, { days });
    toast.success(`Snoozed for ${days} day(s)`);
    fetchNotifications();
  };

  const handleClear = async (id) => {
    await API.patch(`/notifications/${id}/clear`);
    fetchNotifications();
  };

  const handleClearAll = async () => {
    await API.post("/notifications/clear-all");
    fetchNotifications();
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="relative p-2 text-[#787774] hover:text-[#2383E2] transition-colors"
          data-testid="notifications-bell"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#EB5757] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#FFFFFF] border-l border-[#E0E0DE]">
        <SheetHeader>
          <SheetTitle className="text-xl text-[#37352F]">
            Notifications
          </SheetTitle>
          <SheetDescription className="text-[#787774] text-sm">
            Follow-up reminders and updates
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {notifications.length === 0 ? (
            <p className="text-sm text-[#787774] italic">No notifications</p>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                data-testid="clear-all-notifications"
                className="text-xs text-[#EB5757]"
              >
                Clear all
              </Button>
              {notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className="p-3 border border-[#E0E0DE] rounded-sm bg-[#F7F6F3]"
                  data-testid={`notification-${n.notification_id}`}
                >
                  <p className="text-sm text-[#37352F]">{n.message}</p>
                  <p className="text-xs text-[#787774] mt-1">
                    {new Date(n.created_at).toLocaleDateString()}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleSnooze(n.notification_id, 1)}
                      className="text-xs text-[#2383E2] hover:underline flex items-center gap-1"
                    >
                      <Clock className="w-3 h-3" /> 1 day
                    </button>
                    <button
                      onClick={() => handleSnooze(n.notification_id, 2)}
                      className="text-xs text-[#2383E2] hover:underline flex items-center gap-1"
                    >
                      <Clock className="w-3 h-3" /> 2 days
                    </button>
                    <button
                      onClick={() => handleClear(n.notification_id)}
                      className="text-xs text-[#EB5757] hover:underline ml-auto flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
